import {flavorDescriptions, colors, strokeWidth} from '../constants';

import {
  createPoint,
  buildGeometryInstance,
  isSameCoordinatePair,
  getLayerForFlavoredEntityType,
  handleAddCorrectnessIcon,
  hasCoords
} from './entities.utils';

const editOptions = [
  {
    label: 'Toggle shaded area',
    apply: ({board, instance}) => {
      const {onClick} = instance;
      const updatedEntity = {
        ...instance.entity,
        elements: {
          points: [...instance.entity.elements.points].reverse()
        }
      };

      instance.remove();

      return inequality.create({
        board,
        entity: updatedEntity,
        onClick
      });
    }
  },
  {
    label: (instance) => {
      const {inclusive} = instance.entity.attributes;
      return `Make ${inclusive ? 'exclusive' : 'inclusive'}`;
    },
    apply: ({board, instance}) => {
      const {onClick} = instance;
      const updatedEntity = {
        ...instance.entity,
        attributes: {
          ...instance.entity.attributes,
          inclusive: !instance.entity.attributes.inclusive
        }
      };

      instance.remove();

      return inequality.create({
        board,
        entity: updatedEntity,
        onClick
      });
    },
    key: 'toggle-inclusivity'
  }
];

const inequality = {
  /**
   * @example
   * const entity = {
   *   type: 'inequality',
   *   elements: {
   *     points: [
   *       [0, 0],
   *       [4, 1]
   *     ]
   *   },
   *   attributes: {
   *     inclusive: true
   *   },
   *   flavor: 'studentGuess'
   * };
   */

  create({board, entity, onClick}) {
    const {
      flavor,
      elements: {points},
      attributes: {inclusive}
    } = entity;

    const linePoints = points.map((coords) => createPoint({board, coords, flavor}));

    const {color} = flavorDescriptions[flavor];

    const correctnessIcon = handleAddCorrectnessIcon({board, flavor, coords: points[0]});

    const line = board.create('line', linePoints, {
      strokeWidth,
      highlightStrokeWidth: strokeWidth,
      firstArrow: true,
      lastArrow: true,
      strokeColor: color,
      highlightStrokeColor: color,
      dash: inclusive ? 0 : 3,
      touchFirstPoint: true,
      touchLastPoint: true,
      layer: getLayerForFlavoredEntityType('line', flavor),
      tabindex: null
    });

    const inequality = board.create('inequality', [line], {
      fillColor: 'rgb(74, 154, 171)',
      highlightFillColor: 'rgb(74, 154, 171)',
      fillOpacity: 0.2,
      highlightFillOpacity: 0.2,
      strokeWidth: 0,
      tabindex: null
    });

    return buildGeometryInstance({
      board,
      entity,
      geometryElement: inequality,
      constituentElements: [...linePoints, line, inequality],
      onClick,
      editOptions,
      correctnessIcon,
      onRemove: () => {
        board.removeAncestors(line);
      }
    });
  },
  createPartial({board, partialInstance, coords, flavor}) {
    if (!partialInstance) {
      return {
        entity: {
          type: 'inequality',
          elements: {
            points: []
          },
          attributes: {
            inclusive: true
          },
          flavor
        },
        geometryElements: [],
        done: false,
        remove() {
          return null;
        }
      };
    }

    const {
      entity: {
        elements,
        elements: {points}
      }
    } = partialInstance;

    const isValid = !hasCoords(elements, coords);

    if (!isValid) return null;

    const coordPoints = [...points, coords];

    const nextPoint = createPoint({board, coords, flavor});

    const geometryElements = [...partialInstance.geometryElements, nextPoint];

    return {
      entity: {
        type: 'inequality',
        elements: {
          points: coordPoints
        },
        attributes: {
          inclusive: true
        },
        flavor
      },
      geometryElements,
      done: coordPoints.length === 2,
      instructions: 'Complete the inequality by placing a second point.',
      handleCursorMove(cursorCoords) {
        if (board.elementsByName.previewInequality) {
          board.removeAncestors(board.elementsByName.previewInequality);
        }

        if (!coords || !cursorCoords || isSameCoordinatePair(coords, cursorCoords)) {
          return;
        }

        board.create('line', [coords, cursorCoords], {
          name: 'previewInequality',
          strokeWidth,
          highlightStrokeWidth: strokeWidth,
          strokeColor: colors.preview,
          highlightStrokeColor: colors.preview,
          dash: 3,
          firstArrow: true,
          lastArrow: true,
          touchFirstPoint: true,
          touchLastPoint: true,
          tabindex: null
        });
      },
      remove() {
        geometryElements.forEach((element) => board.removeAncestors(element));
        return null;
      }
    };
  }
};

export default inequality;
